import { handleHttpRequest, http, type HttpResponse } from 'interfaces'
import { type Chamber, type State } from 'components'
import { type Role, type Country, type BaseUrl, type BasePhone, type BaseJurisdiction } from 'services'
import { type CommitteeMember } from 'pages'

export interface CommitteeBaseInformation {
  id: number
  name: string
}

export interface CommitteeJurisdiction extends BaseJurisdiction {
  committeeId: number
}

export interface CommitteeUrl extends BaseUrl {
  committeeId: number
}

export interface UsPhone extends BasePhone {
  typeId: number
  areaCode: string
  description?: string
}

export interface CommitteePhone extends UsPhone {
  committeeId: number
}

export interface CommitteeBasicInformation extends CommitteeBaseInformation {
  parent: CommitteeBaseInformation
  chamber: Chamber
  role: Role
  state: State
  displayName: string
  code: string
  active: boolean
  addresses: CommitteeAddress[]
  lastUpdated: string
  updatedBy: string
  urls: CommitteeUrl[]
  responsibilities: number[]
}

export interface UsAddress {
  building: string
  room: string
  addressOne: string
  addressTwo?: string
  city: string
  state: State | null
  country: Country | null
  zipCode: string
}

export interface BaseAddress {
  id: number
  typeId: number
  primary: UsAddress
  secondary: UsAddress
  mailingAddress: 1 | 2
  displayOrder: number
  updatedBy?: string
  lastUpdated?: string
  phones: UsPhone[]
  isStaff?: boolean
}

export interface CommitteeAddress extends BaseAddress {
  committeeId: number
  phones: CommitteePhone[]
}

export async function getComBasicInfo (comId: number): Promise<CommitteeBasicInformation> {
  return await http
    .get(`/api/v1/us/committee/${comId}/`, {
    }).then(value => {
      const data: CommitteeBasicInformation = value.data
      return data
    })
}

export async function updateComBasicInfo (comId: number, payload: any): Promise<HttpResponse<CommitteeBasicInformation>> {
  return await handleHttpRequest(http.patch(`/api/v1/us/committee/${comId}/`, payload))
}

export async function deleteCommittee (comId: number): Promise<HttpResponse<CommitteeBasicInformation>> {
  return await handleHttpRequest(http.delete(`/api/v1/us/committee/${comId}/`))
}

export async function getComAddress (comId: number): Promise<CommitteeBasicInformation> {
  return await http.get(`/api/v1/us/committee/${comId}/address/`).then(response => {
    const originalResponse = response

    originalResponse.data.addresses = response.data.addresses.map((data: any) => {
      const transformAddress = (prefix: string): UsAddress => ({
        building: data[`${prefix}Building`],
        room: data[`${prefix}Room`],
        addressOne: data[`${prefix}StreetAddress`],
        addressTwo: data[`${prefix}StreetAddressTwo`],
        city: data[`${prefix}City`],
        state: data[`${prefix}State`],
        country: data[`${prefix}Country`],
        zipCode: data[`${prefix}Zipcode`]
      })

      return {
        id: data.id,
        committeeId: data.committeeId,
        primary: transformAddress('primary'),
        secondary: transformAddress('secondary'),
        mailingAddress: data.mailingAddress,
        phones: data.phones,
        displayOrder: data.displayOrder,
        updatedBy: data.updatedBy,
        lastUpdated: data.lastUpdated
      }
    })
    return originalResponse.data
  })
}

export async function updateComAddress (comId: number, addressId: number, payload: CommitteeAddress): Promise<HttpResponse<CommitteeAddress>> {
  const revertAddress = (address: UsAddress, prefix: string): any => ({
    [`${prefix}Building`]: address.building,
    [`${prefix}Room`]: address.room,
    [`${prefix}StreetAddress`]: address.addressOne,
    [`${prefix}StreetAddressTwo`]: address.addressTwo,
    [`${prefix}City`]: address.city,
    [`${prefix}State`]: address.state,
    [`${prefix}Country`]: address.country,
    [`${prefix}Zipcode`]: address.zipCode
  })

  const revertedPayload = {
    id: payload.id,
    committeeId: payload.committeeId,
    ...revertAddress(payload.primary, 'primary'),
    ...revertAddress(payload.secondary, 'secondary'),
    mailingAddress: payload.mailingAddress,
    displayOrder: payload.displayOrder,
    updatedBy: payload.updatedBy,
    phones: payload.phones.map(phone => ({
      ...phone,
      id: phone.id != null && phone.id < 0 ? 0 : phone.id
    })),
    lastUpdated: payload.lastUpdated
  }

  return await handleHttpRequest(
    http.patch(`/api/v1/us/committee/${comId}/address/${addressId}/`, revertedPayload)
  )
}

export async function getChildCommittees (comId: number): Promise<CommitteeBaseInformation[]> {
  return await http
    .get(`/api/v1/us/committee/${comId}/hierarchy/`, {
    }).then(value => {
      const data: CommitteeBaseInformation[] = value.data
      return data
    })
}

export async function updateCommitteeUrl (url: CommitteeUrl): Promise<HttpResponse<CommitteeUrl>> {
  return await handleHttpRequest(http.patch(`/api/v1/us/committee/${url.committeeId}/url/${url.id}/`, { value: url.value }))
}

export async function createCommitteeUrl (url: CommitteeUrl): Promise<HttpResponse<CommitteeUrl>> {
  return await handleHttpRequest(http.post(`/api/v1/us/committee/${url.committeeId}/url/`, url))
}

export async function deleteCommitteeUrl (url: CommitteeUrl): Promise<boolean> {
  const urlPath = `/api/v1/us/committee/${url.committeeId}/url/${url.id}/`
  return await http.delete(urlPath)
    .then(() => { return true })
    .catch(() => { return false })
}

export async function getCommitteeMembers (comId: string): Promise<Record<string, CommitteeMember[]>> {
  return await http
    .get(`/api/v1/us/committee/${comId}/member/`, {
    }).then(value => {
      const data: Record<string, CommitteeMember[]> = value.data
      return data
    })
}

export async function patchCommitteeMembersDisplayOrder (comId: number, orderedMembers: CommitteeMember[], selectedMember: CommitteeMember): Promise<HttpResponse<CommitteeMember[]>> {
  return await handleHttpRequest(http.patch(`/api/v1/us/committee/${comId}/member/`, {
    orderedMembers,
    selectedMember
  }))
}

export interface CommitteeCreateResponse {
  id: number
}

export async function createCommittee (payload: any): Promise<HttpResponse<CommitteeCreateResponse>> {
  return await handleHttpRequest(http.post('/api/v1/us/committee/', payload))
}

export async function getCommitteeJurisdiction (comId: number): Promise<HttpResponse<CommitteeJurisdiction>> {
  return await handleHttpRequest(http.get(`/api/v1/us/committee/${comId}/jurisdiction/`))
}

export async function updateCommitteeJurisdiction<T extends CommitteeJurisdiction> (jurisdiction: T): Promise<HttpResponse<T>> {
  return await handleHttpRequest(http.patch(`/api/v1/us/committee/${jurisdiction.committeeId}/jurisdiction/`, jurisdiction))
}
